import React, {FC, useEffect, useState} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {Button, CircularProgress, useTheme} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import FfairModal from "view/components/modal/FfairModal";
import FlexBox from "view/components/Layout/FlexBox";
import {useFieldArray, useForm} from "react-hook-form";
import Form from "view/components/Form";
import {Currency, IStage, SurchargeType} from "@evennt-tech/api-v2-core";
import AddOrEdit from "view/apps/Supplier/pages/shared/SurchargeModal/Components/AddOrEdit";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import WarningIcon from "view/images/WarningIcon";
import Alert from "view/components/Alert";
import {useSurcharge} from "store/surcharge/surcharge.store";
import {ICreateSurchargeReq, IOneSurcharge} from "@evennt-tech/api-v2-external";
import {
  isApplyToAllSelection,
  ProductsApplyTo,
} from "view/apps/Supplier/pages/shared/productsSelection";

import Product from "ts/api/product";
import useStyles from "./style";
import List from "./Components/List";
import {useComposeStagesAndOverwritten, useOverwrittenProducts} from "./shared";

const OVERWRITTEN_LIST_ID = "surcharge-overwritten-products-list";

type SurchargeModalProps = {
  products: Product[];
  applyTo?: ProductsApplyTo;
  targetCount?: number;
  onClose: () => void;
  currency: Currency;
  companyId: string;
  showId?: string;
  onUpdate?: () => void;
};

const SurchargeModal: FC<SurchargeModalProps> = ({
  products,
  applyTo,
  targetCount,
  onClose,
  currency,
  companyId,
  showId,
  onUpdate,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const {
    surcharges,
    createSurcharge,
    deleteSurcharges,
    fetchSurcharges,
    clearSurcharges,
    isLoading,
  } = useSurcharge();

  const productsScope: ProductsApplyTo = applyTo ?? {
    productIds: products.map((product) => product.id),
  };

  useEffect(() => {
    fetchSurcharges({
      showId,
      supplierCompanyId: companyId,
      ...productsScope,
    });
    return () => {
      clearSurcharges();
    };
  }, [products, applyTo, companyId, showId]);

  const [isAllDisabled, setIsAllDisabled] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isOpenWarning, setIsOpenWarning] = useState(false);
  const [isAddNew, setIsAddNew] = useState(false);
  const [stages, overwrittenProductIds] = useComposeStagesAndOverwritten(
    surcharges,
    targetCount ?? products.length
  );
  const {
    labels: overwrittenLabels,
    hasMore: hasMoreOverwritten,
    isLoading: isLoadingOverwritten,
    loadMore: loadMoreOverwritten,
  } = useOverwrittenProducts({
    productIds: overwrittenProductIds,
    supplierCompanyId: companyId,
    showId,
    isEnabled: isListOpen,
  });

  const form = useForm<{stages: IStage[]}>();
  const {control, handleSubmit, getValues, setValue, formState} = form;

  const {fields, replace, remove} = useFieldArray({
    control,
    name: "stages",
  });

  useEffect(() => {
    if (stages?.length && !fields.length && !formState.isDirty) {
      setValue("stages", stages, {shouldDirty: true});
    }
  }, [stages, fields, setValue, formState.isDirty]);

  const onCancelAllStages = async () => {
    setIsOpenWarning(false);
    setIsAllDisabled(true);

    await deleteSurcharges({
      supplierCompanyId: companyId,
      showId,
      ...productsScope,
    });

    onUpdate?.();
    onClose();
  };

  const onSubmit = async () => {
    setIsOpenWarning(false);
    setIsAllDisabled(true);

    const stages = getValues().stages.map((stage) => {
      if (stage.type === SurchargeType.unpublish) {
        return stage;
      }
      return {...stage, amount: Number(stage.amount)};
    });

    const toStagePayload = (
      stage: IStage,
      productId?: string
    ): IOneSurcharge => ({
      daysBeforeBuild: stage.daysBeforeBuild,
      type: stage.type,
      ...(stage.type !== SurchargeType.unpublish && {amount: stage.amount}),
      ...(productId && {productId}),
    });

    const surcharges: IOneSurcharge[] = isApplyToAllSelection(productsScope)
      ? stages.map((stage) => toStagePayload(stage))
      : products.flatMap(({id}) =>
          stages.map((stage) => toStagePayload(stage, id))
        );

    const {productIds: _productIds, ...applyToAllScope} = productsScope;
    const surchargesDTO: ICreateSurchargeReq = {
      showId,
      supplierCompanyId: companyId,
      surcharges,
      ...applyToAllScope,
    };
    await createSurcharge(surchargesDTO);

    onUpdate?.();
    onClose();
  };

  const onTryToSubmit = handleSubmit(() => {
    if (overwrittenProductIds.length) {
      setIsOpenWarning(true);
      return;
    }
    return onSubmit();
  });

  const isUnpublishTaken =
    fields.findIndex((stage) => stage.type === SurchargeType.unpublish) !== -1;

  const takenDates = fields.map((p) => p.daysBeforeBuild);

  return (
    <>
      <FfairModal open onClose={onClose}>
        <Form
          noDefaultStyles
          className={classes.root}
          form={form}
          onSubmit={onTryToSubmit}
        >
          <FlexBox column gap={2}>
            <span className={classes.title}>Surcharge</span>
            <FlexBox alignCenter justifyContent="space-between">
              <span className={classes.subtitle}>Surcharge Stage</span>
              <Button
                color="primary"
                onClick={() => setIsAddNew(true)}
                disabled={isAddNew || isAllDisabled || isLoading}
              >
                <AddIcon />
                Add New
              </Button>
            </FlexBox>
            {isLoading ? (
              <FlexBox alignCenter justifyCenter>
                <CircularProgress />
              </FlexBox>
            ) : (
              <List
                isUnpublishTaken={isUnpublishTaken}
                fields={fields}
                currency={currency}
                takenDates={takenDates}
                remove={remove}
                update={(index, stage) => {
                  const stages = [...fields] as IStage[];
                  stages.splice(index, 1, stage);
                  const sortedStages = stages.sort(sortStages);
                  replace(sortedStages);
                }}
              />
            )}
            {isAddNew && (
              <AddOrEdit
                takenDates={takenDates}
                type="add"
                isUnpublishTaken={isUnpublishTaken}
                currency={currency}
                onSubmit={(stage) => {
                  const sortedStages = [...fields, stage].sort(sortStages);
                  replace(sortedStages);
                  setIsAddNew(false);
                }}
                onCancel={() => setIsAddNew(false)}
              />
            )}
            {!!overwrittenProductIds.length && (
              <FlexBox p={2} className={classes.errorListRoot} gap={2} column>
                <FlexBox
                  column
                  gap={2}
                  alignCenter
                  onClick={() => setIsListOpen((prev) => !prev)}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <FlexBox gap={2} alignCenter>
                    <WarningIcon />
                    <span>Some Products already have Surcharge options</span>
                  </FlexBox>
                  <FlexBox gap={1} alignCenter>
                    <span className={classes.seeList}>See the list </span>
                    {isListOpen ? (
                      <ExpandLessIcon htmlColor={theme.palette.primary.main} />
                    ) : (
                      <ExpandMoreIcon htmlColor={theme.palette.primary.main} />
                    )}
                  </FlexBox>
                </FlexBox>
                {isListOpen && (
                  <div
                    id={OVERWRITTEN_LIST_ID}
                    className={classes.overwrittenList}
                  >
                    {!overwrittenLabels.length && isLoadingOverwritten ? (
                      <FlexBox alignCenter justifyCenter p={1}>
                        <CircularProgress size={20} />
                      </FlexBox>
                    ) : (
                      <InfiniteScroll
                        dataLength={overwrittenLabels.length}
                        next={loadMoreOverwritten}
                        hasMore={hasMoreOverwritten}
                        loader={
                          <FlexBox alignCenter justifyCenter p={1}>
                            <CircularProgress size={20} />
                          </FlexBox>
                        }
                        scrollableTarget={OVERWRITTEN_LIST_ID}
                      >
                        <FlexBox column gap={1}>
                          {overwrittenLabels.map((item, index) => (
                            <span key={`${item}-${index}`}>
                              {index + 1}. {item}
                            </span>
                          ))}
                        </FlexBox>
                      </InfiniteScroll>
                    )}
                  </div>
                )}
              </FlexBox>
            )}
            <FlexBox alignCenter justifyContent="space-between" mt={1}>
              <Button
                onClick={onCancelAllStages}
                disabled={isAllDisabled}
                className={classes.cancelSurchargeButton}
              >
                Cancel Surcharge
              </Button>
              <FlexBox alignCenter gap={2}>
                <Button
                  color="primary"
                  disabled={isAllDisabled}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={isAllDisabled || !fields.length}
                  onClick={onTryToSubmit}
                >
                  Submit
                </Button>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </Form>
      </FfairModal>
      <FfairModal open={isOpenWarning} onClose={() => setIsOpenWarning(false)}>
        <FlexBox column gap={3} className={classes.warningRoot}>
          <span className={classes.warningTitle}>Set up Surcharge</span>
          <Alert severity="warning">
            {overwrittenProductIds.length} product surcharges are already set.
          </Alert>
          <span className={classes.warningContent}>
            Press <b>'Submit'</b> to overwrite the existing surcharges, or{" "}
            <b>'Cancel'</b> to retain them.
          </span>
          <FlexBox alignCenter justifyContent="end" gap={2}>
            <Button color="primary" onClick={() => setIsOpenWarning(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={onSubmit}>
              Submit
            </Button>
          </FlexBox>
        </FlexBox>
      </FfairModal>
    </>
  );
};

export const sortStages = (a: IStage, b: IStage) =>
  a.daysBeforeBuild > b.daysBeforeBuild ? -1 : 1;

export default SurchargeModal;
